import { Schedule as ScheduleIcon } from '@mui/icons-material';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  type TextFieldProps,
  Typography,
} from '@mui/material';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';

import {
  getTimezoneAbbreviation,
  PresetDateFormats,
  startOfDay,
  toDateTime,
} from 'helpers/datetime';

import { useTimezoneContext } from 'components/timezones';

import getTimePlaceholder from './getTimePlaceholder';

const HOURS_INTERVAL = 15;
// 24h * 60min / 15min = 96 time slots per day
const TIME_SLOTS_PER_DAY = (24 * 60) / HOURS_INTERVAL;

function formatTime(
  date?: Date | null,
  format?: PresetDateFormats,
  timezone?: string,
) {
  if (!date) return '';
  const dateTime = toDateTime(date);
  const zoned = timezone ? dateTime.setZone(timezone) : dateTime;
  return zoned.toFormat(format ?? 'hh:mm a')!.toLowerCase();
}

type Props = Omit<
  TextFieldProps,
  'onBlur' | 'onChange' | 'placeholder' | 'value'
> & {
  format?: PresetDateFormats;
  onBlur?: (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>,
    nextDate: Date | null,
  ) => void;
  onChange?: (date: Date | null) => void;
  timezone?: string;
  timezoneAbbreviation?: string;
  value?: Date | null;
};

const TimeField = forwardRef(
  (
    {
      value: controlledValue,
      onChange,
      format,
      timezone,
      timezoneAbbreviation,
      ...props
    }: Props,
    ref,
  ) => {
    const anchorRef = useRef<HTMLDivElement | null>(null);
    const { currentTimeZoneAbbreviation } = useTimezoneContext();
    // Precedence: explicit prop > derived from timezone > context
    const displayedAbbreviation =
      timezoneAbbreviation ??
      (timezone
        ? getTimezoneAbbreviation(timezone)
        : currentTimeZoneAbbreviation);
    // TextField only supports 'small' | 'medium'; clamp so the icon doesn't outgrow the input
    const iconSize: 'small' | 'medium' =
      props.size === 'small' ? 'small' : 'medium';

    const options = useMemo(() => {
      const baseDate = startOfDay(undefined, { timezone });

      return new Array(TIME_SLOTS_PER_DAY).fill(undefined).map((_, index) => {
        const date: Date = new Date(baseDate);
        date.setMinutes(index * HOURS_INTERVAL);
        return formatTime(date, format, timezone);
      });
    }, [format, timezone]);

    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [value, setValue] = useState<string>(
      formatTime(controlledValue, format, timezone),
    );

    const fallback = useRef<string>();
    const hasError = value !== '' && !getTimePlaceholder(value, { timezone });

    useEffect(() => {
      setValue(formatTime(controlledValue, format, timezone));
    }, [controlledValue, format, timezone]);

    const handleChange = (value: Date | null) => {
      onChange?.(value);
    };

    const handleInternalChange = (value: string): Date | null => {
      const date =
        getTimePlaceholder(value, { timezone }) ??
        getTimePlaceholder(fallback.current ?? '', { timezone });

      setValue(formatTime(date, format, timezone));
      handleChange(date);
      return date;
    };

    return (
      <>
        <TextField
          {...props}
          error={hasError || props.error}
          InputProps={{
            ...props.InputProps,
            endAdornment: (
              <Box alignItems="center" display="flex" gap={0.5}>
                <Typography
                  fontStyle="italic"
                  variant="caption"
                  whiteSpace="nowrap"
                >
                  {displayedAbbreviation}
                </Typography>
                <IconButton
                  onClick={() => setIsMenuOpen(true)}
                  size={iconSize}
                  tabIndex={-1}
                >
                  <ScheduleIcon fontSize={iconSize} />
                </IconButton>
              </Box>
            ),
            ref,
            style: {
              ...props.InputProps?.style,
              paddingRight: 'unset',
            },
          }}
          onBlur={(e) => {
            const next = handleInternalChange(value);
            props.onBlur?.(e, next);
          }}
          onChange={(e) => setValue(e.target.value ?? '')}
          onFocus={(e) => {
            e.target.select();
            fallback.current = value;
            props.onFocus?.(e);
          }}
          placeholder="hh:mm (a|p)m"
          ref={anchorRef}
          value={value}
        />
        <Menu
          anchorEl={anchorRef.current}
          anchorOrigin={{
            horizontal: 'right',
            vertical: 'bottom',
          }}
          onClose={() => setIsMenuOpen(false)}
          open={isMenuOpen}
          PaperProps={{ sx: { width: '200px' } }}
          transformOrigin={{
            horizontal: 'right',
            vertical: 'top',
          }}
        >
          {options.map((value) => (
            <MenuItem
              key={value}
              onClick={() => {
                handleInternalChange(value);
                setIsMenuOpen(false);
              }}
            >
              {value}
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  },
);

export default TimeField;
